import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { toast } from 'sonner'
import { Clock, Landmark, Link2, Send, Trash2, TriangleAlert } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FieldLabel } from '@/components/FieldLabel'
import { SearchSelect } from '@/components/SearchSelect'
import { AdvertisersApi, type Advertiser } from '@/services/advertisers/advertisersApi'
import { PoliticalEntitiesApi, type PoliticalSponsor } from '@/services/political/politicalEntitiesApi'
import { useNetwork } from '@/contexts/NetworkContext'
import { useCurrentUser } from '@/contexts/UserContext'
import { apiErrorDescription } from '@/services/core/apiError'
import { formatDateTime } from '@/utils/format'

interface AdvertiserInviteDialogProps {
  open: boolean
  onClose: () => void
  advertiser: Advertiser
}

export const politicalLinkRequestQueryKey = (advertiserId: string) => [
  'advertiser-political-link-request',
  advertiserId,
]

/**
 * Connects an advertiser to a political party: asks a sponsor or controller
 * on the political network to link up, which emails the political users of
 * its owner team. Only one request can be open at a time, so an open one is
 * shown instead, with a way to withdraw it.
 */
export function AdvertiserInviteDialog({ open, onClose, advertiser }: AdvertiserInviteDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const defaultValues = { organisationId: '' }

  const { networks } = useNetwork()
  const user = useCurrentUser()
  // A political network the user runs is listed directly, TEST organisations and all.
  const politicalNetworkTagId = networks.find(
    (n) => n.politicalApp && user?.permissionsByNetwork[n.tagId]?.manageSystem,
  )?.tagId

  const linkRequestKey = politicalLinkRequestQueryKey(advertiser.id)
  const {
    data: linkRequest,
    isLoading: linkRequestLoading,
    isError: linkRequestFailed,
    error: linkRequestError,
  } = useQuery({
    queryKey: linkRequestKey,
    queryFn: () => AdvertisersApi.getPoliticalLinkRequest(advertiser.id),
    enabled: open,
  })
  const canRequest = !linkRequestLoading && !linkRequestFailed && !linkRequest

  const { data: organisations = [], isLoading: organisationsLoading } = useQuery({
    queryKey: ['political-sponsors', politicalNetworkTagId ?? null],
    queryFn: () => PoliticalEntitiesApi.getSponsors(politicalNetworkTagId),
    enabled: open && canRequest,
    staleTime: 5 * 60 * 1000,
  })

  const mutation = useMutation({
    mutationFn: (organisation: PoliticalSponsor) =>
      AdvertisersApi.requestPoliticalLink(advertiser.id, { idOfPoliticalEntity: organisation.publicId }),
    onSuccess: (_, organisation) => {
      toast.success(t('advertisers.inviteSent'), {
        description: t('advertisers.inviteSentDescription', { name: organisation.name }),
        duration: 3000,
      })
      queryClient.invalidateQueries({ queryKey: linkRequestKey })
      handleClose()
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => AdvertisersApi.deletePoliticalLinkRequest(advertiser.id),
    onSuccess: () => {
      toast.success(t('advertisers.inviteRemoved'), { duration: 3000 })
      queryClient.setQueryData(linkRequestKey, null)
    },
  })

  const form = useForm({
    defaultValues,
    onSubmit: ({ value }) => {
      const organisation = organisations.find((o) => o.publicId === value.organisationId)
      if (organisation) mutation.mutate(organisation)
    },
  })
  const organisationId = useStore(form.store, (s) => s.values.organisationId)
  const selected = organisations.find((o) => o.publicId === organisationId)

  const handleClose = () => {
    form.reset(defaultValues)
    mutation.reset()
    removeMutation.reset()
    onClose()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (canRequest) form.handleSubmit()
  }

  const error = mutation.error ?? removeMutation.error ?? linkRequestError

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Link2 className="inline size-4 mr-2" />
            {t('advertisers.inviteTitle')}
          </DialogTitle>
          <DialogDescription>{t('advertisers.inviteDescription', { name: advertiser.name })}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-3.5">
          {linkRequestLoading && (
            <p className="text-sm text-muted-foreground">{t('advertisers.inviteChecking')}</p>
          )}

          {linkRequest && (
            <Alert>
              <Clock className="size-4" />
              <AlertDescription className="flex flex-col items-start gap-2.5">
                <span>
                  {t('advertisers.invitePending', {
                    name: linkRequest.name,
                    expiry: formatDateTime(linkRequest.expiry),
                  })}
                </span>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  loading={removeMutation.isPending}
                  onClick={() => removeMutation.mutate()}
                >
                  <Trash2 className="size-4" />
                  {t('advertisers.inviteRemove')}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {canRequest && (
            <div className="p-field">
              <FieldLabel tooltip={t('advertisers.inviteSponsorHelp')}>{t('advertisers.inviteSponsor')}</FieldLabel>
              <form.Field name="organisationId">
                {(field) => (
                  <SearchSelect
                    value={field.state.value}
                    onChange={field.handleChange}
                    options={organisations.map((o) => ({
                      value: o.publicId,
                      label: o.contactEmail ? `${o.name} — ${o.contactEmail}` : o.name,
                    }))}
                    placeholder={t('advertisers.inviteSponsorPlaceholder')}
                    searchPlaceholder={t('advertisers.inviteSponsorSearch')}
                    emptyText={t('advertisers.inviteNoSponsors')}
                    loading={organisationsLoading}
                    icon={Landmark}
                  />
                )}
              </form.Field>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <TriangleAlert className="size-4" />
              <AlertDescription>{apiErrorDescription(error)}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={handleClose} disabled={mutation.isPending}>
              {canRequest ? t('common.cancel') : t('common.close')}
            </Button>
            {canRequest && (
              <Button type="submit" size="sm" loading={mutation.isPending} disabled={!selected}>
                <Send className="size-4" />
                {t('advertisers.inviteSend')}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
